// ─── Tarife: Funktionen, die ein Tarif freischaltet ─────────────────────────
//
// Abgrenzung zu den Berechtigungen (permissions.ts): eine BERECHTIGUNG regelt,
// was eine Person in einem Stammbaum tun darf – vergeben vom Besitzer. Eine
// FUNKTION regelt, ob es die Sache auf dieser Installation für diesen Kunden
// überhaupt gibt – vergeben über den Tarif.
//
// Beides muss stimmen. Wer „Archiv bearbeiten" darf, aber im Freitarif ohne
// Archiv sitzt, kommt nicht hinein; und umgekehrt.
//
// Maßgeblich ist immer der Tarif des BESITZERS eines Stammbaums, nicht der des
// Handelnden. Sonst hinge der Funktionsumfang eines Baums davon ab, wer gerade
// darin arbeitet – und ein Mitglied im teuren Tarif würde dem Besitzer
// Funktionen verschaffen, für die der nicht zahlt.
//
// Was NICHT hier steht, gibt es immer: der Stammbaum selbst, die Suche, das
// Anlegen und Bearbeiten von Personen und Beziehungen, die Mitgliederverwaltung
// und die eigenen Kontoeinstellungen. Ein Tarif, der den Stammbaum abschalten
// kann, wäre kein Tarif, sondern eine Sperre.

export interface FeatureDefinition {
  key: string;
  label: string;
  description: string;
  /** Grobe Einordnung für die Darstellung. */
  group: 'Export' | 'Inhalte' | 'Auswertung' | 'Ansichten' | 'Spiele' | 'Daten';
}

export const FEATURES: FeatureDefinition[] = [
  // ─── Export ───────────────────────────────────────────────────────────────
  {
    key: 'export.gedcom',
    label: 'GEDCOM-Export',
    description: 'Stammbaum im Austauschformat anderer Ahnenprogramme herunterladen.',
    group: 'Export',
  },
  {
    key: 'export.csv',
    label: 'CSV-Export',
    description: 'Personen als Tabelle herunterladen.',
    group: 'Export',
  },
  {
    key: 'export.pdf',
    label: 'PDF-Druck',
    description: 'Den Stammbaum mehrseitig als PDF ausgeben.',
    group: 'Export',
  },
  {
    key: 'export.svg',
    label: 'SVG-Export',
    description: 'Die Baumansicht als Vektorgrafik speichern.',
    group: 'Export',
  },
  {
    key: 'chronik',
    label: 'Chronik (Stammbuch)',
    description:
      'Den ganzen Stammbaum als gebundenes PDF ausgeben – Titelblatt, ' +
      'Inhaltsverzeichnis, eine Seite je Person, Quellenverzeichnis.',
    group: 'Export',
  },

  // ─── Inhalte ──────────────────────────────────────────────────────────────
  {
    key: 'archive',
    label: 'Archiv',
    description: 'Fotos und Dokumente hochladen und Personen zuordnen.',
    group: 'Inhalte',
  },
  {
    key: 'community',
    label: 'Forum',
    description:
      'Zugang zum gemeinsamen Forum – Fragen an andere Forschende, Lesehilfe für alte ' +
      'Handschriften, Hinweise zu Archiven. Anders als alle übrigen Funktionen gehört sie zum ' +
      'KONTO und nicht zu einem Stammbaum: das Forum steht über den Stammbäumen, und aus ihm ' +
      'ist nichts aus einem Stammbaum sichtbar.',
    group: 'Inhalte',
  },
  {
    key: 'medical',
    label: 'Medizin & Vererbung',
    description:
      'Diagnosen und Merkmale pflegen und auswerten. Gesundheitsdaten – zusätzlich zur Freischaltung braucht jede Person eine ausdrückliche Berechtigung.',
    group: 'Inhalte',
  },
  {
    key: 'sourceCoverage',
    label: 'Belege',
    description: 'Übersicht, welche Angaben belegt sind und welche noch nicht.',
    group: 'Inhalte',
  },
  {
    key: 'plausibility',
    label: 'Plausibilitätsprüfung',
    description: 'Widersprüche aufspüren – Geburt nach dem Tod, unmögliche Alter.',
    group: 'Inhalte',
  },

  // ─── Auswertung ───────────────────────────────────────────────────────────
  {
    key: 'statistics',
    label: 'Statistiken',
    description: 'Auswertungen über Namen, Orte, Lebensdauer und Berufe.',
    group: 'Auswertung',
  },
  {
    key: 'lifelines',
    label: 'Lebenslinien',
    description: 'Zeitstrahl über die Generationen.',
    group: 'Auswertung',
  },
  {
    key: 'relationshipFinder',
    label: 'Pfadfinder',
    description: 'Verwandtschaftswege zwischen zwei Personen berechnen.',
    group: 'Auswertung',
  },
  {
    key: 'calendar',
    label: 'Familienkalender',
    description: 'Geburts- und Hochzeitstage im Jahresverlauf.',
    group: 'Auswertung',
  },
  {
    key: 'coldCases',
    // Der Schlüssel bleibt – er steht in Tarifen und Rollen bestehender
    // Installationen. Nur was der Mensch liest, hat sich geändert.
    label: 'Offene Fragen',
    description: 'Sammelt, was im Stammbaum fehlt, und wo die Antwort zu finden ist.',
    group: 'Auswertung',
  },

  // ─── Ansichten ────────────────────────────────────────────────────────────
  {
    key: 'fanChart',
    label: 'Fächerdiagramm',
    description: 'Die Ahnenreihe als Fächer statt als Baum.',
    group: 'Ansichten',
  },
  {
    key: 'cosmos',
    label: 'Sternenhimmel',
    description: 'Der Stammbaum als Sternbild.',
    group: 'Ansichten',
  },
  {
    key: 'map',
    label: 'Kartenansicht',
    description: 'Personen auf einer Landkarte nach ihren Orten.',
    group: 'Ansichten',
  },

  // ─── Spiele ───────────────────────────────────────────────────────────────
  {
    key: 'quiz',
    label: 'Familien-Quiz',
    description: 'Fragen zur eigenen Familie, automatisch erzeugt.',
    group: 'Spiele',
  },
  {
    key: 'achievements',
    label: 'Trophäenraum',
    description: 'Auszeichnungen für Meilensteine in der Forschung.',
    group: 'Spiele',
  },
  {
    key: 'fogOfWar',
    label: 'Entdecker-Karte',
    description: 'Erforschte und unerforschte Zweige als Landkarte.',
    group: 'Spiele',
  },

  // ─── Daten ────────────────────────────────────────────────────────────────
  {
    key: 'import',
    label: 'GEDCOM-Import',
    description: 'Bestehende Stammbäume einlesen.',
    group: 'Daten',
  },
  {
    key: 'treeBackup',
    label: 'Eigene Sicherung',
    description: 'Einen einzelnen Stammbaum sichern und wiederherstellen.',
    group: 'Daten',
  },
];

export const ALL_FEATURES: string[] = FEATURES.map((f) => f.key);
const FEATURE_SET = new Set(ALL_FEATURES);

export function isValidFeature(key: string): boolean {
  return FEATURE_SET.has(key);
}

/**
 * Alte Schlüssel auf neue abbilden.
 *
 * 'games' war einmal EIN Schalter für Quiz, Trophäenraum und Entdecker-Karte.
 * Die Migration löst ihn auf; diese Tabelle ist die zweite Sicherung, falls
 * doch noch irgendwo ein alter Wert auftaucht – etwa aus einer eingespielten
 * Sicherung oder einem alten API-Aufruf.
 */
const ALTLASTEN: Record<string, string[]> = {
  games: ['quiz', 'achievements', 'fogOfWar'],
};

/** Funktionsliste eines Tarifs: alte Schlüssel auflösen, Unbekanntes verwerfen. */
export function normalisiereFeatures(features: string[]): string[] {
  const raus = new Set<string>();
  for (const f of features) {
    for (const neu of ALTLASTEN[f] ?? [f]) {
      if (FEATURE_SET.has(neu)) raus.add(neu);
    }
  }
  return [...raus];
}

/** Der Tarif eines Kontos, so wie ihn Anwendung und Konsole sehen. */
export interface PlanInfo {
  id: string;
  name: string;
  description: string | null;
  /** Wie viele eigene Stammbäume. */
  maxTrees: number;
  /** Speichergrenze für ALLE Stammbäume des Kontos zusammen. null = unbegrenzt. */
  maxStorageBytes: number | null;
  /** Höchstzahl der Personen JE Stammbaum. null = unbegrenzt. */
  maxPersons: number | null;
  /** Freigeschaltete Funktionen (Schlüssel aus FEATURES). */
  features: string[];
  isDefault: boolean;
}
